import alertSignal from "signals/Alert.signal";
import api from "api/api";
import { SEDI_DEMOGRAPHICS_RELATED_BUSINESS_STATUS } from "../Constant/businessCompanyInfoDropDowns";
import { Chart as ChartJS } from "chart.js";
// @ts-ignore
import pdfMake from "pdfmake";
// @ts-ignore
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { enumCaseToTabCase } from "libs/string";
import { camelCaseToTitleText, pascalCaseToTitleCase } from "utils/formatTxt";
import { AdhocReportQuery } from "types";
import $appSettings from "signals/AppSettings.signal";

const apiFetch = async (options: any) => api.get({ path: '/query', options });

type ApiParams = {
  model: string;
  fn: string;
  count: string | Record<string, string[]>;
  periodicity: string;
  startOfTime: string;
  where?: Record<string, any>;
};

export type AdvancedFilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'before'
  | 'after';

export type AdvancedFilterFieldType = 'string' | 'number' | 'boolean' | 'date';

export type AdvancedFilter = {
  field: string;
  operator: AdvancedFilterOperator;
  value: string;
  type?: AdvancedFilterFieldType;
};

export const ADVANCED_FILTER_OPERATOR_LABELS: Record<AdvancedFilterOperator, string> = {
  equals: 'Equals',
  notEquals: 'Not Equals',
  contains: 'Contains',
  in: 'In',
  gt: 'Greater Than',
  gte: 'Greater Than Or Equal',
  lt: 'Less Than',
  lte: 'Less Than Or Equal',
  before: 'Before',
  after: 'After',
};

type AdvancedFilterField = {
  value: string;
  label: string;
  type: AdvancedFilterFieldType;
  enumOptions?: Array<{ value: string; label: string }>;
};

const APPLICATION_ADVANCED_FILTER_FIELDS: AdvancedFilterField[] = [
  { value: 'status', label: 'Status', type: 'string' },
  { value: 'fundingProgramId', label: 'Funding Program', type: 'number' },
  { value: 'fundingProgramMembershipId', label: 'Funding Program Membership', type: 'number' },
  { value: 'lenderId', label: 'Lender ID', type: 'number' },
  { value: 'businessId', label: 'Business ID', type: 'number' },
  { value: 'sediBusiness', label: 'SEDI Business', type: 'boolean' },
  { value: 'isActive', label: 'Is Active', type: 'boolean' },
  { value: 'submittedByEdo', label: 'Submitted By EDO', type: 'boolean' },
  { value: 'approvedOnCommit', label: 'Approved On Commit', type: 'boolean' },
  { value: 'governmentGuaranteeProgram', label: 'Government Guarantee Program', type: 'boolean' },
  { value: 'secured', label: 'Secured', type: 'boolean' },
  { value: 'first500Calcap', label: 'First 500 CALCAP', type: 'boolean' },
  { value: 'refinancing', label: 'Refinancing', type: 'boolean' },
  {
    value: 'deliveryMethod',
    label: 'Delivery Method',
    type: 'string',
    enumOptions: [
      { value: 'E', label: 'E - Email' },
      { value: 'M', label: 'M - Mail' },
      { value: 'F', label: 'F - Fax' },
      { value: 'I', label: 'I - In-Person' },
    ],
  },
  { value: 'programName', label: 'Program Name', type: 'string' },
  { value: 'loanPurpose', label: 'Loan Purpose', type: 'string' },
  { value: 'loanPurposeSecondary', label: 'Loan Purpose Secondary', type: 'string' },
  {
    value: 'loanType',
    label: 'Loan Type',
    type: 'string',
    enumOptions: [
      { value: 'L', label: 'L - Line of Credit' },
      { value: 'T', label: 'T - Term Loan' },
      { value: 'R', label: 'R - TRAC Lease' },
    ],
  },
  { value: 'interestRate', label: 'Interest Rate', type: 'number' },
  { value: 'loanAPR', label: 'Loan APR', type: 'number' },
  { value: 'loanTerm', label: 'Loan Term', type: 'number' },
  { value: 'askedAmount', label: 'Asked Amount', type: 'number' },
  { value: 'approvedAmount', label: 'Approved Amount', type: 'number' },
  { value: 'calcapAmount', label: 'CALCAP Amount', type: 'number' },
  { value: 'calcapPercentage', label: 'CALCAP Percentage', type: 'number' },
  { value: 'otherProgramPercentage', label: 'Other Program Percentage', type: 'number' },
  { value: 'premiumAmount', label: 'Premium Amount', type: 'number' },
  { value: 'borrowerPremiumAmount', label: 'Borrower Premium Amount', type: 'number' },
  { value: 'calcapPremiumAmount', label: 'CALCAP Premium Amount', type: 'number' },
  { value: 'jobsCreated', label: 'Jobs Created', type: 'number' },
  { value: 'jobsRetained', label: 'Jobs Retained', type: 'number' },
  { value: 'receivedDate', label: 'Received Date', type: 'date' },
  { value: 'committedDate', label: 'Committed Date', type: 'date' },
  { value: 'approvedDate', label: 'Approved Date', type: 'date' },
  { value: 'disbursementDate', label: 'Disbursement Date', type: 'date' },
  { value: 'createdDate', label: 'Created Date', type: 'date' },
  { value: 'updatedDate', label: 'Updated Date', type: 'date' },
  { value: 'businessData.nonProfit', label: 'Business Data - Non Profit', type: 'boolean' },
  { value: 'businessData.minorityOwned', label: 'Business Data - Minority Owned', type: 'boolean' },
  { value: 'businessData.womenOwned', label: 'Business Data - Women Owned', type: 'boolean' },
  { value: 'businessData.businessOpened2Years', label: 'Business Data - Opened 2 Years', type: 'boolean' },
  { value: 'businessData.severelyAffected', label: 'Business Data - Severely Affected', type: 'boolean' },
  { value: 'businessData.minorityOrWomenOwned', label: 'Business Data - Minority or Women Owned', type: 'boolean' },
  { value: 'businessData.naicsCode', label: 'Business Data - NAICS Code', type: 'string' },
  { value: 'businessData.censusTractNumber', label: 'Business Data - Census Tract', type: 'string' },
  { value: 'businessData.county', label: 'Business Data - County', type: 'string' },
  { value: 'businessData.countyCode', label: 'Business Data - County Code', type: 'number' },
  { value: 'businessData.zipCode', label: 'Business Data - ZIP', type: 'string' },
  { value: 'businessData.numberOfEmployees', label: 'Business Data - Number Of Employees', type: 'number' },
  { value: 'businessData.numberOfFtes', label: 'Business Data - Number Of FTEs', type: 'number' },
  { value: 'businessData.annualRevenue', label: 'Business Data - Annual Revenue', type: 'number' },
  { value: 'businessData.expectedJobsCreated', label: 'Business Data - Expected Jobs Created', type: 'number' },
  { value: 'businessData.expectedJobsRetained', label: 'Business Data - Expected Jobs Retained', type: 'number' },
  { value: 'sedi.businessNotCertified', label: 'SEDI - Business Not Certified', type: 'boolean' },
  { value: 'sedi.businessOwnedCredit', label: 'SEDI - Business Owned Credit', type: 'boolean' },
  { value: 'sedi.businessOwnedCDFI', label: 'SEDI - Business Owned CDFI', type: 'boolean' },
  { value: 'sedi.businessBuild', label: 'SEDI - Business Build', type: 'boolean' },
  { value: 'sedi.businessBuildArea', label: 'SEDI - Business Build Area', type: 'boolean' },
  {
    value: 'tribalGovernmentProgramTransactionType',
    label: 'Tribal Government Program Transaction Type',
    type: 'string',
    enumOptions: [
      {
        value: 'IN_JURISDICTION_BUSINESS_TRIBAL_LANDS',
        label: 'In-jurisdiction transaction with business on tribal lands',
      },
      {
        value: 'IN_JURISDICTION_BUSINESS_STATES_TRIBE_PHYSICALLY_LOCATED',
        label:
          'In-jurisdiction transaction with business in states where the Tribe is physically located or within which the Tribe exercises jurisdiction',
      },
      {
        value: 'IN_JURISDICTION_TRIBAL_ENTERPRISE_OPERATED_BUSINESS',
        label:
          'In-jurisdiction transaction with Tribal enterprise-operated business, business owned by Tribal members, or business in a state in which Tribal members reside',
      },
      {
        value: 'OUT_OF_JURISDICTION',
        label:
          'Out-of-jurisdiction transaction - transaction which does not fit into any of the categories above',
      },
      { value: 'NOT_APPLICABLE', label: 'N/A' },
    ],
  },
  {
    value: 'demographics.ethnicity',
    label: 'Demographics - Ethnicity',
    type: 'string',
    enumOptions: [
      { value: 'HISPANIC_OR_LATINO', label: 'Hispanic or Latino' },
      { value: 'NOT_HISPANIC_OR_LATINO', label: 'Not Hispanic or Latino' },
      { value: 'PREFER_NOT_TO_RESPOND', label: 'Prefer not to respond' },
      { value: 'H', label: 'Hispanic or Latino (Legacy)' },
      { value: 'NH', label: 'Not Hispanic or Latino (Legacy)' },
      { value: 'P', label: 'Prefer not to respond (Legacy)' },
    ],
  },
  { value: 'demographics.race', label: 'Demographics - Race', type: 'string' },
  {
    value: 'demographics.ancestry',
    label: 'Demographics - Ancestry',
    type: 'string',
    enumOptions: [
      {
        value: 'MIDDLE_EASTERN_OR_NORTH_AFRICAN_ANCESTRY',
        label: 'Middle Eastern or North African',
      },
      {
        value: 'NOT_MIDDLE_EASTERN_OR_NORTH_AFRICAN_ANCESTRY',
        label: 'Not Middle Eastern or North African',
      },
      { value: 'PREFER_NOT_TO_RESPOND', label: 'Prefer not to respond' },
      { value: 'M', label: 'Middle Eastern or North African (Legacy)' },
      { value: 'NM', label: 'Not Middle Eastern or North African (Legacy)' },
      { value: 'P', label: 'Prefer not to respond (Legacy)' },
    ],
  },
  {
    value: 'demographics.orientation',
    label: 'Demographics - Orientation',
    type: 'string',
    enumOptions: [
      { value: 'GAY_OR_LESBIAN', label: 'Gay or Lesbian' },
      { value: 'BISEXUAL', label: 'Bisexual' },
      { value: 'STRAIGHT', label: 'Straight' },
      { value: 'SOMETHING_ELSE', label: 'Something else' },
      { value: 'PREFER_NOT_TO_RESPOND', label: 'Prefer not to respond' },
      { value: 'GL', label: 'Gay or Lesbian (Legacy)' },
      { value: 'B', label: 'Bisexual (Legacy)' },
      { value: 'S', label: 'Straight (Legacy)' },
      { value: 'SE', label: 'Something else (Legacy)' },
      { value: 'P', label: 'Prefer not to respond (Legacy)' },
    ],
  },
  {
    value: 'demographics.gender',
    label: 'Demographics - Gender',
    type: 'string',
    enumOptions: [
      { value: 'FEMALE', label: 'Female' },
      { value: 'MALE', label: 'Male' },
      { value: 'NONBINARY', label: 'Nonbinary' },
      { value: 'PREFER_TO_SELF_DESCRIBE', label: 'Prefer to self-describe' },
      { value: 'F', label: 'Female (Legacy)' },
      { value: 'M', label: 'Male (Legacy)' },
      { value: 'N', label: 'Nonbinary (Legacy)' },
      { value: 'PS', label: 'Prefer to self-describe (Legacy)' },
    ],
  },
  { value: 'demographics.genderPreference', label: 'Demographics - Gender Preference', type: 'string' },
  { value: 'demographics.genderDescribe', label: 'Demographics - Gender Describe', type: 'string' },
  {
    value: 'demographics.veteranOwned',
    label: 'Demographics - Veteran Owned',
    type: 'string',
    enumOptions: [
      { value: 'Y', label: 'Yes' },
      { value: 'N', label: 'No' },
      { value: 'D', label: 'Declined' },
    ],
  },
  {
    value: 'demographics.disabledVeteran',
    label: 'Demographics - Disabled Veteran',
    type: 'string',
    enumOptions: [
      { value: 'Y', label: 'Yes' },
      { value: 'N', label: 'No' },
      { value: 'D', label: 'Declined' },
    ],
  },
];

const DEFAULT_ADVANCED_FILTER_OPERATORS: Record<AdvancedFilterFieldType, AdvancedFilterOperator[]> = {
  string: ['equals', 'notEquals', 'contains', 'in'],
  number: ['equals', 'notEquals', 'in', 'gt', 'gte', 'lt', 'lte'],
  boolean: ['equals', 'notEquals'],
  date: ['equals', 'before', 'after', 'gte', 'lte'],
};

function nextCalendarDayIso(yyyyMmDd: string): string {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + 1);
  const yy = date.getUTCFullYear();
  const mm = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const dd = `${date.getUTCDate()}`.padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function isApplicationAdvancedDateFilterField(field: string): boolean {
  return APPLICATION_ADVANCED_FILTER_FIELDS.some(
    (f) => f.value === field && f.type === 'date'
  );
}

type GetAdvancedFilterFieldsOptions = {
  /** Date columns only apply cleanly to time-series reports; omit from dropdown when false. */
  includeDateFields?: boolean;
};

export function getAdvancedFilterFields(
  model: string,
  options?: GetAdvancedFilterFieldsOptions
): AdvancedFilterField[] {
  if (model !== 'Application') {
    return [];
  }

  const statusOptions = Object.values(
    $appSettings.value.constants.APPLICATION_STATUS || {}
  )
    .filter(Boolean)
    .map((status) => ({
      value: `${status}`,
      label: enumCaseToTabCase(`${status}`),
    }));

  let fields = APPLICATION_ADVANCED_FILTER_FIELDS.map((field) => {
    if (field.value !== 'status') {
      return field;
    }

    return {
      ...field,
      enumOptions: statusOptions,
    };
  });

  if (options?.includeDateFields === false) {
    fields = fields.filter((f) => f.type !== 'date');
  }

  return fields;
}

export function formatAdvancedFilterSummary(
  filters: AdvancedFilter[],
  model: string
): string[] {
  const fieldsByValue = getAdvancedFilterFields(model).reduce((acc, field) => {
    acc[field.value] = field;
    return acc;
  }, {} as Record<string, AdvancedFilterField>);

  return filters
    .filter((filter) => !!filter.field && filter.value !== '')
    .map((filter) => {
      const field = fieldsByValue[filter.field];
      const fieldLabel = field?.label || filter.field;
      const operatorLabel =
        ADVANCED_FILTER_OPERATOR_LABELS[filter.operator] || filter.operator;
      let valueLabel = filter.value;
      if (field?.enumOptions && field.enumOptions.length > 0) {
        const matched = field.enumOptions.find((option) => option.value === filter.value);
        valueLabel = matched?.label || filter.value;
      }
      return `${fieldLabel} ${operatorLabel} ${valueLabel}`;
    });
}

export function getAdvancedFilterOperators(
  type: AdvancedFilterFieldType
): AdvancedFilterOperator[] {
  return DEFAULT_ADVANCED_FILTER_OPERATORS[type] || DEFAULT_ADVANCED_FILTER_OPERATORS.string;
}

/**
 * Validates user input for number-type advanced filters only.
 * For operator `in`, comma-separated values must each parse to a finite number.
 * Empty / whitespace-only tokens are invalid (avoids `Number('') === 0`).
 */
export function isValidAdvancedFilterNumberInput(
  rawValue: string,
  operator: AdvancedFilterOperator
): boolean {
  if (operator === 'in') {
    const parts = rawValue
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    if (parts.length === 0) {
      return false;
    }
    return parts.every((part) => Number.isFinite(Number(part)));
  }

  const trimmed = rawValue.trim();
  if (trimmed === '') {
    return false;
  }
  return Number.isFinite(Number(trimmed));
}

/**
 * Strips characters that cannot belong in a numeric filter value (typing / paste).
 * `in` allows commas and spaces between tokens; both allow decimal, negative, and JS number literals (e.g. 1e3).
 */
export function sanitizeAdvancedFilterNumberInput(
  rawValue: string,
  operator: AdvancedFilterOperator
): string {
  if (operator === 'in') {
    return rawValue.replace(/[^0-9,.\-+eE\s]/g, '');
  }
  return rawValue.replace(/[^0-9.\-+eE]/g, '');
}

function castValueByType(rawValue: string, type: AdvancedFilterFieldType): any {
  if (type === 'boolean') {
    return rawValue === 'true';
  }

  if (type === 'number') {
    const parsed = Number(rawValue);
    return Number.isNaN(parsed) ? rawValue : parsed;
  }

  if (type === 'date') {
    const trimmed = rawValue.trim().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : rawValue;
  }

  return rawValue;
}

function setNestedValue(target: Record<string, any>, path: string[], value: any): Record<string, any> {
  if (path.length === 0) {
    return target;
  }

  const [first, ...rest] = path;
  if (rest.length === 0) {
    target[first] = value;
    return target;
  }

  if (!target[first]) {
    target[first] = {};
  }

  target[first] = setNestedValue(target[first], rest, value);
  return target;
}

function normalizeRelationWhereClause(whereClause: Record<string, any>) {
  const normalized = { ...whereClause };
  ['businessData', 'sedi', 'demographics'].forEach((relation) => {
    if (normalized[relation] && !normalized[relation].is) {
      normalized[relation] = { is: normalized[relation] };
    }
  });
  return normalized;
}

function toOperatorClause(
  operator: AdvancedFilterOperator,
  rawValue: string,
  type: AdvancedFilterFieldType
) {
  const casted = castValueByType(rawValue, type);
  const listValue = rawValue
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => castValueByType(v, type));

  switch (operator) {
    case 'equals':
      return casted;
    case 'notEquals':
      return { not: casted };
    case 'contains':
      return { contains: rawValue, mode: 'insensitive' };
    case 'in':
      return { in: listValue.length > 0 ? listValue : [casted] };
    case 'gt':
    case 'after':
      return { gt: casted };
    case 'gte':
      return { gte: casted };
    case 'lt':
    case 'before':
      return { lt: casted };
    case 'lte':
      return { lte: casted };
    default:
      return casted;
  }
}

export function buildAdvancedFilterWhereClause(
  filters: AdvancedFilter[]
): Record<string, any> | undefined {
  const modelFields = APPLICATION_ADVANCED_FILTER_FIELDS.reduce(
    (acc, field) => ({ ...acc, [field.value]: field.type }),
    {} as Record<string, AdvancedFilterFieldType>
  );

  const clauses = filters
    .filter(({ field, value }) => field && value !== '')
    .flatMap(({ field, operator, value, type }) => {
      const fieldType = type || modelFields[field] || 'string';
      const path = field.split('.');

      if (fieldType === 'date' && operator === 'equals') {
        const day = castValueByType(value, 'date') as string;
        if (typeof day !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) {
          const clause = toOperatorClause(operator, value, fieldType);
          const whereClause = setNestedValue({}, path, clause);
          return [normalizeRelationWhereClause(whereClause)];
        }
        const nextDay = nextCalendarDayIso(day);
        const gteClause = setNestedValue({}, path, { gte: day });
        const ltClause = setNestedValue({}, path, { lt: nextDay });
        return [
          normalizeRelationWhereClause(gteClause),
          normalizeRelationWhereClause(ltClause),
        ];
      }

      const clause = toOperatorClause(operator, value, fieldType);
      const whereClause = setNestedValue({}, path, clause);
      return [normalizeRelationWhereClause(whereClause)];
    });

  if (clauses.length === 0) {
    return undefined;
  }

  if (clauses.length === 1) {
    return clauses[0];
  }

  return {
    AND: clauses,
  };
}

export async function fetchAdhocDataset(params: ApiParams): Promise<any> {
  try {
    const { fn, model } = params;
    // @ts-ignore. Does not understand keyof
    const queryConfig = FILTERING_CONFIG[fn][model];
    return apiFetch({ ...params, model: model.split('.')[0], sum: queryConfig.sum || undefined, join: queryConfig.join || undefined });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: 'An error occurred while fetching the dataset.',
    });
  }
}

export const MODELS = [
  {
    label: 'Application',
    value: 'Application',
  },
  {
    label: 'Business',
    value: 'Business',
  },
  {
    label: 'Fund',
    value: 'Fund',
  },
  {
    label: 'Lender',
    value: 'Lender',
  },
  {
    label: 'Program Request',
    value: 'ProgramRequest',
  },
  {
    label: 'Venture Capitalist',
    value: 'VentureCapitalFirm',
  },
  {
    label: 'Obligated Funds',
    value: 'MoneyDistribution.Obligated'
  },
  {
    label: 'Expended Funds',
    value: 'MoneyDistribution.Expended'
  }
];

export const getModels = (
  portalType: string
): { value: string; label: string }[] => {
  const { PORTAL_TYPES } = $appSettings.value.constants;
  const models = [...MODELS];
  if (portalType === PORTAL_TYPES.lender) {
    const exclude = ['Fund', 'VentureCapitalFirm', 'Lender', 'MoneyDistribution.Obligated', 'MoneyDistribution.Expended'];
    return models.filter(({ value }) => !exclude.includes(value));
  }

  if (portalType === PORTAL_TYPES.vc) {
    const exclude = ['Application', 'VentureCapitalFirm', 'Lender', 'MoneyDistribution.Obligated', 'MoneyDistribution.Expended'];
    return models.filter(({ value }) => !exclude.includes(value));
  }

  if (portalType === PORTAL_TYPES.business) {
    const exclude = ['Business', 'ProgramRequest', 'Fund', 'MoneyDistribution.Obligated', 'MoneyDistribution.Expended'];
    return models.filter(({ value }) => !exclude.includes(value));
  }

  return models;
};

const businessCountByOrGroupBy = {
  'sediCertification->>selfCertifiedSEDIDemographicsRelatedBusinessStatus':
    'sediCertification->>selfCertifiedSEDIDemographicsRelatedBusinessStatus',
  'demographics->>gender':
    'demographics->>gender',
  'demographics->>veteranStatus':
    'demographics->>veteranStatus',
  'demographics->>sexualOrientation':
    'demographics->>sexualOrientation',
  'minorityStatus->>isWomanOwned':
    'minorityStatus->>isWomanOwned',
  'minorityStatus->>isMinorityOwned':
    'minorityStatus->>isMinorityOwned',
  'minorityStatus->>isVeteranOwned':
    'minorityStatus->>isVeteranOwned',
  "demographics->'ethnicity'->>hispanicOrLatino":
    "demographics->'ethnicity'->>hispanicOrLatino",
  'zipCode': 'zipCode',
  'censusTract': 'censusTract'
}

export const REPORT_TYPES = [
  {
    label: 'Sum By',
    value: 'sumBy',
  },
  {
    label: 'Count By',
    value: 'groupBy',
  },
  {
    label: 'Time Series',
    value: 'timeSeries',
  },
];

export const filteringBaseOptions = {
  status: 'status',
  fundingProgramId: 'fundingProgramId',
  lenderId: 'lenderId',
};

export const FILTERING_CONFIG = {
  timeSeries: {
    Application: {
      createdDate: 'createdDate',
    },
    ProgramRequest: {
      createdDate: 'createdDate',
    },
    Lender: {
      createdDate: 'createdDate',
    },
    Business: {
      createdDate: 'createdDate',
    },
    VentureCapitalFirm: {
      createdDate: 'createdDate',
    },
    Fund: {
      createdDate: 'createdDate',
    },
  },
  sumBy: {
    ['MoneyDistribution.Obligated']: {
      sum: 'obligated',
      ...businessCountByOrGroupBy,
    },
    ['MoneyDistribution.Expended']: {
      sum: 'expended',
      ...businessCountByOrGroupBy,
    },
  },
  groupBy: {
    Fund: {
      ventureCapitalFirmId: 'ventureCapitalFirmId',
    },
    Application: {
      ...filteringBaseOptions,
      sediBusiness: 'sediBusiness',
      ...businessCountByOrGroupBy,
    },
    ProgramRequest: {
      ...filteringBaseOptions,
      ventureCapitalFirmId: 'ventureCapitalFirmId',
    },
    Lender: {
      name: 'name',
      lenderType: 'lenderType',
    },
    VentureCapitalFirm: {
      name: 'name',
      lenderType: 'lenderType',
    },
    Business: {
      ...businessCountByOrGroupBy
    },
  },
};

export const GRAPH_LABEL_CONFIG = {
  status: ({ status }: { status: string }): string => enumCaseToTabCase(status),
  businessId: ({ name }: { name: string }): string => name,
  lenderId: ({ name }: { name: string }): string => name,
  ventureCapitalFirmId: ({ name }: { name: string }): string => name,
  fundingProgramId: ({ name }: { name: string }): string => name,
  sediBusiness: (
    row: Record<string, boolean> & { sum: string }
  ): string => {
    const countValue = row.sediBusiness;
    return countValue === true ? 'SEDI' : 'non SEDI';
  },
  'minorityStatus->>isVeteranOwned': (
    row: Record<string, string> & { sum: string }
  ): string => {
    const countValue = row['?column?'];
    if (!countValue) {
      return 'Not Provided';
    }

    const labelValuePairs = {
      yes: 'Veteran Owned',
      no: 'Not Veteran Owned',
      decline: 'Declined',
      Y: 'Veteran Owned',
      N: 'Not Veteran Owned',
      D: 'Declined',
      true: 'Veteran Owned',
      false: 'Not Veteran Owned',
    }

    // @ts-ignore
    return labelValuePairs[countValue] || countValue;
  },
  'minorityStatus->>isMinorityOwned': (
    row: Record<string, string> & { sum: string }
  ): string => {
    const countValue = row['?column?'];
    if (!countValue) {
      return 'Not Provided';
    }

    const labelValuePairs = {
      yes: 'Minority Owned',
      no: 'Not Minority Owned',
      decline: 'Declined',
      Y: 'Minority Owned',
      N: 'Not Minority Owned',
      D: 'Declined',
      true: 'Minority Owned',
      false: 'Not Minority Owned',
    }

    // @ts-ignore
    return labelValuePairs[countValue] || countValue;
  },
  'minorityStatus->>isWomanOwned': (
    row: Record<string, string> & { sum: string }
  ): string => {
    const countValue = row['?column?'];
    if (!countValue) {
      return 'Not Provided';
    }

    const labelValuePairs = {
      yes: 'Woman Owned',
      no: 'Not Woman Owned',
      decline: 'Declined',
      Y: 'Woman Owned',
      N: 'Not Woman Owned',
      D: 'Declined',
      true: 'Woman Owned',
      false: 'Not Woman Owned',
    }

    // @ts-ignore
    return labelValuePairs[countValue] || countValue;
  },
  "demographics->'ethnicity'->>hispanicOrLatino": (
    row: Record<string, string> & { sum: string }
  ): string => {
    const countValue = row['?column?'];
    if (!countValue) {
      return 'Not Provided';
    }

    const labelValuePairs = {
      yes: 'Hispanic or Latino',
      no: 'Non Hispanic or Latino',
      decline: 'Declined',
      HISPANIC_OR_LATINO: 'Hispanic or Latino',
      NOT_HISPANIC_OR_LATINO: 'Non Hispanic or Latino',
      PREFER_NOT_TO_RESPOND: 'Declined',
      H: 'Hispanic or Latino',
      NH: 'Non Hispanic or Latino',
      P: 'Declined',
    }

    // @ts-ignore
    return labelValuePairs[countValue] || countValue;
  },
  'demographics->>veteranStatus': (
    row: Record<string, string> & { sum: string }
  ): string => {
    const countValue = row['?column?'];
    if (!countValue) {
      return 'Not Provided';
    }

    const labelValuePairs = {
      yes: 'Veteran',
      no: 'Non Veteran',
      decline: 'Declined',
      Y: 'Veteran',
      N: 'Non Veteran',
      D: 'Declined',
    }

    // @ts-ignore
    return labelValuePairs[countValue] || countValue;
  },
  'demographics->>sexualOrientation': (
    row: Record<string, string> & { sum: string }
  ): string => {
    const countValue = row['?column?'];
    if (!countValue) {
      return 'Not Provided';
    }

    const labelValuePairs = {
      lesbianOrGay: 'Gay or Lesbian',
      gayOrLesbian: 'Gay or Lesbian',
      bisexual: 'Bisexual',
      straight: 'Straight',
      somethingElse: 'Something else',
      decline: 'Declined',
      GAY_OR_LESBIAN: 'Gay or Lesbian',
      BISEXUAL: 'Bisexual',
      STRAIGHT: 'Straight',
      SOMETHING_ELSE: 'Something else',
      PREFER_NOT_TO_RESPOND: 'Declined',
      GL: 'Gay or Lesbian',
      B: 'Bisexual',
      S: 'Straight',
      SE: 'Something else',
      P: 'Declined',
    }

    // @ts-ignore
    return labelValuePairs[countValue] || countValue;
  },
  'demographics->>gender': (
    row: Record<string, string> & { sum: string }
  ): string => {
    const countValue = row['?column?'];
    if (!countValue) {
      return 'Not Provided';
    }

    const labelValuePairs = {
      nonBinary: 'Non Binary',
      male: 'Male',
      female: 'Female',
      decline: 'Declined',
      FEMALE: 'Female',
      MALE: 'Male',
      NONBINARY: 'Non Binary',
      PREFER_TO_SELF_DESCRIBE: 'Self Described',
      F: 'Female',
      M: 'Male',
      N: 'Non Binary',
      PS: 'Self Described',
    }

    // @ts-ignore
    return labelValuePairs[countValue] || countValue;
  },
  'sediCertification->>selfCertifiedSEDIDemographicsRelatedBusinessStatus': (
    row: Record<string, string> & { count: string }
  ): string => {
    const countValue = row['?column?'];
    if (!countValue) {
      return 'Not Provided';
    }

    const labelValuePairs = Object.values(
      SEDI_DEMOGRAPHICS_RELATED_BUSINESS_STATUS
    );

    const matchingPair = labelValuePairs.find(
      ({ value }) => countValue.includes(value)
    );
    if (!matchingPair) {
      return countValue;
    }

    return matchingPair.label;
  },
};

export const getFilteringOptions = (
  portalType: string
): Record<string, any> => {
  const { PORTAL_TYPES } = $appSettings.value.constants;

  const filteringOptions = {
    sumBy: { ...FILTERING_CONFIG.sumBy },
    groupBy: { ...FILTERING_CONFIG.groupBy },
    timeSeries: { ...FILTERING_CONFIG.timeSeries },
  };

  if (portalType !== PORTAL_TYPES.edo) {
    // @ts-ignore
    delete filteringOptions.groupBy.Lender.lenderType;
    // @ts-ignore
    delete filteringOptions.groupBy.VentureCapitalFirm.lenderType;
    // @ts-ignore
    delete filteringOptions.groupBy.ProgramRequest.lenderId;
    // @ts-ignore
    delete filteringOptions.groupBy.ProgramRequest.ventureCapitalFirmId;
    // @ts-ignore
    delete filteringOptions.sumBy['MoneyDistribution.Obligated'];
    // @ts-ignore
    delete filteringOptions.sumBy['MoneyDistribution.Expended'];
  }

  return {
    sumBy: Object.fromEntries(
      Object.entries(filteringOptions.sumBy).map(([key, value]) => [
        key,
        Object.keys(value),
      ])
    ),
    groupBy: Object.fromEntries(
      Object.entries(filteringOptions.groupBy).map(([key, value]) => [
        key,
        Object.keys(value),
      ])
    ),
    timeSeries: Object.fromEntries(
      Object.entries(filteringOptions.timeSeries).map(([key, value]) => [
        key,
        Object.keys(value),
      ])
    ),
  };
};

export function generateChartImagePdf(chart: ChartJS): any {
  const canvas = chart.canvas;
  const imageURL = canvas.toDataURL('image/png');
  const docDefinition = {
    content: [
      {
        image: imageURL,
        width: 480,
        height: 480,
      },
    ],
  };

  const pdf = pdfMake.createPdf(docDefinition);
  return pdf.download();
}

export function exportChartData(data: Record<string, string>[]): any {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const csvURL = window.URL.createObjectURL(blob);
  const downloadLink = document.createElement('a');
  downloadLink.href = csvURL;
  downloadLink.setAttribute('download', 'adhoc-report.csv');
  downloadLink.click();
}

type ExportChartDataXlsxOptions = {
  filters?: string[];
  title?: string;
};

export function exportChartDataXlsx(
  data: Record<string, any>[],
  options: ExportChartDataXlsxOptions = {}
): void {
  const { filters = [], title = 'Adhoc Report' } = options;
  const headerRows = [
    ['Report Title', title],
    ['Applied Filters', filters.length > 0 ? filters.join(' | ') : 'None'],
    [],
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(headerRows);
  XLSX.utils.sheet_add_json(worksheet, data, {
    origin: 'A4',
    skipHeader: false,
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

  const filtersRows = [
    { Item: 'Report Title', Value: title },
    { Item: 'Applied Filters', Value: filters.length > 0 ? `${filters.length}` : '0' },
    ...(filters.length > 0
      ? filters.map((filter, index) => ({
        Item: `Filter ${index + 1}`,
        Value: filter,
      }))
      : [{ Item: 'Filter 1', Value: 'None' }]),
  ];
  const filtersWorksheet = XLSX.utils.json_to_sheet(filtersRows);
  XLSX.utils.book_append_sheet(workbook, filtersWorksheet, 'Filters');

  XLSX.writeFile(workbook, 'adhoc-report.xlsx');
}

export function mapColumnIdToLabel(value: string): string {
  if (value === 'createdDate') {
    return 'Number of';
  }

  if (value === 'sediBusiness') {
    return 'SEDI Business';
  }

  if (value === 'fundingProgramId') {
    return 'Program';
  }

  if (value === 'lenderId') {
    return 'Lender';
  }

  if (value === 'businessId') {
    return 'Business';
  }

  if (value === 'ventureCapitalFirmId') {
    return 'Venture Capitalist';
  }

  if (value.includes('SEDIDemographics')) {
    return 'SEDI Demographics';
  }

  if (value.includes('demographics') && value.includes('gender')) {
    return 'Demographics - Gender';
  }

  if (value.includes('demographics') && value.includes('veteranStatus')) {
    return 'Demographics - Veteran Status';
  }

  if (value.includes('demographics') && value.includes('sexualOrientation')) {
    return 'Demographics - Sexual Orientation';
  }

  if (value.includes('minorityStatus') && value.includes('isWomanOwned')) {
    return 'Minority Status - Woman Owned';
  }

  if (value.includes('minorityStatus') && value.includes('isMinorityOwned')) {
    return 'Minority Status - Minority Owned';
  }

  if (value.includes('minorityStatus') && value.includes('isVeteranOwned')) {
    return 'Minority Status - Veteran Owned';
  }

  if (value.includes('demographics') && value.includes('hispanicOrLatino')) {
    return 'Demographics - Hispanic or Latino (Ethnicity)';
  }

  return camelCaseToTitleText(value);
}

export const mapQueryToWords = ({
  model,
  fn,
  count,
}: AdhocReportQuery): string => {
  if (fn === 'sumBy') {
    if (model.includes('Obligated')) {
      return `Obligated Funds by ${mapColumnIdToLabel(count)}`
    }

    if (model.includes('Expended')) {
      return `Expended Funds by ${mapColumnIdToLabel(count)}`
    }

    return `Funds by ${mapColumnIdToLabel(count)}`
  }

  if (fn === 'groupBy') {
    return `${pascalCaseToTitleCase(model)} by ${mapColumnIdToLabel(count)}`;
  }

  if (fn === 'timeSeries') {
    return `${pascalCaseToTitleCase(model)} Time Series`;
  }

  return '';
};

export const PERIODICITY_OPTIONS = [
  {
    value: 'day',
    label: 'Day',
  },
  {
    value: 'week',
    label: 'Week',
  },
  {
    value: 'month',
    label: 'Month',
  },
];

export const START_OF_TIME_OPTIONS = {
  day: [2, 3, 5, 7, 15, 30].map((e) => ({
    value: `${e - 1} day`,
    label: `${e} days`,
  })),
  week: [2, 4, 8, 12].map((e) => ({
    value: `${e - 1} week`,
    label: `${e} weeks`,
  })),
  month: [2, 3, 6, 9, 12].map((e) => ({
    value: `${e - 1} month`,
    label: `${e} months`,
  })),
};

